import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { IosShareOutlined as IosShareOutlinedIcon } from "../components/icons/FigmaControlIcons";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

const RAW_API_BASE = (process.env.REACT_APP_API_BASE || "").trim();
const PURPLE = "#9C4FFF";
const BLUE = "#3451F4";

function buildApiUrl(pathname) {
  const path = pathname?.startsWith("/") ? pathname : `/${pathname || ""}`;
  let base = RAW_API_BASE ? RAW_API_BASE.replace(/\/$/, "") : "/api";
  const hasApiSuffix = base === "/api" || base.endsWith("/api") || base.includes("/api/");
  if (!hasApiSuffix) base = `${base}/api`;
  return `${base}${path}`;
}

function safeTrim(value) {
  if (value === undefined || value === null) return "";
  return String(value).trim();
}

function formatTimeline(startDate, endDate) {
  const start = safeTrim(startDate);
  const end = safeTrim(endDate);
  if (start && end) return `${start} - ${end}`;
  return start || end || "";
}

const formFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "16px",
    backgroundColor: "#FFFFFF",
    "& fieldset": {
      borderColor: "rgba(15, 23, 42, 0.10)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(52, 81, 244, 0.24)",
    },
    "&.Mui-focused fieldset": {
      borderColor: BLUE,
      borderWidth: 2,
    },
  },
  "& .MuiInputBase-input": {
    fontFamily: "Be Vietnam Pro",
    fontSize: 14,
  },
  "& .MuiInputBase-inputMultiline": {
    fontFamily: "Be Vietnam Pro",
    fontSize: 14,
    lineHeight: 1.6,
  },
};

export default function CandidatePublicSharePage() {
  const { token = "" } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [profile, setProfile] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    requesterName: "",
    requesterEmail: "",
    companyName: "",
    requesterTitle: "",
    requesterPhone: "",
    companyWebsite: "",
    message: "",
  });

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError("This shared profile link is invalid.");
      return;
    }

    let active = true;
    setLoading(true);
    setError("");

    fetch(buildApiUrl(`/public/candidate-shares/${encodeURIComponent(token)}`))
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.error || "Failed to load shared profile.");
        }
        return payload;
      })
      .then((payload) => {
        if (!active) return;
        setProfile(payload);
      })
      .catch((fetchError) => {
        if (!active) return;
        console.error("Failed to load shared candidate profile:", fetchError);
        setError(fetchError?.message || "Failed to load shared profile.");
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [token]);

  const candidate = profile?.candidate || null;
  const share = profile?.share || null;

  const topSkills = useMemo(
    () => (Array.isArray(candidate?.skills) ? candidate.skills.filter(Boolean).slice(0, 12) : []),
    [candidate?.skills]
  );

  const highlights = useMemo(
    () =>
      (Array.isArray(candidate?.highlights) ? candidate.highlights : [])
        .map((item) => safeTrim(item))
        .filter(Boolean),
    [candidate?.highlights]
  );

  const handleChange = (field) => (event) => {
    setForm((previous) => ({
      ...previous,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");
    setSubmitSuccess("");
    setSubmitting(true);

    try {
      const response = await fetch(
        buildApiUrl(`/public/candidate-shares/${encodeURIComponent(token)}/request-intro`),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(form),
        }
      );

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || "Failed to submit intro request.");
      }

      setSubmitSuccess(
        payload?.message || "Your intro request has been sent to the RPO AI team."
      );
      setForm({
        requesterName: "",
        requesterEmail: "",
        companyName: "",
        requesterTitle: "",
        requesterPhone: "",
        companyWebsite: "",
        message: "",
      });
    } catch (submitErr) {
      console.error("Failed to submit intro request:", submitErr);
      setSubmitError(submitErr?.message || "Failed to submit intro request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top left, rgba(156,79,255,0.15), transparent 28%), radial-gradient(circle at bottom right, rgba(52,81,244,0.12), transparent 24%), linear-gradient(180deg, #F8FAFF 0%, #EEF2FF 100%)",
        px: { xs: 2, sm: 3, md: 5 },
        py: { xs: 3, md: 5 },
      }}
    >
      <Box sx={{ maxWidth: 1240, mx: "auto" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            mb: { xs: 3, md: 4 },
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "14px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                background: "linear-gradient(135deg, #3451F4 0%, #9C4FFF 100%)",
                boxShadow: "0 14px 28px rgba(52, 81, 244, 0.2)",
              }}
            >
              <IosShareOutlinedIcon sx={{ fontSize: 20 }} />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontFamily: "Be Vietnam Pro",
                  fontSize: 13,
                  fontWeight: 800,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: BLUE,
                }}
              >
                Shared Through RPO AI
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Be Vietnam Pro",
                  fontSize: { xs: 18, sm: 22 },
                  fontWeight: 800,
                  color: "#101828",
                }}
              >
                Candidate Introduction
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <Chip
              icon={<LockOutlinedIcon sx={{ color: `${BLUE} !important` }} />}
              label="Contact details stay private"
              sx={{
                height: 36,
                borderRadius: "999px",
                backgroundColor: "#FFFFFF",
                border: "1px solid rgba(52,81,244,0.12)",
                color: BLUE,
                fontFamily: "Be Vietnam Pro",
                fontWeight: 700,
                px: 1,
              }}
            />
            <Chip
              icon={<HandshakeOutlinedIcon sx={{ color: `${PURPLE} !important` }} />}
              label="Requests route through our team"
              sx={{
                height: 36,
                borderRadius: "999px",
                backgroundColor: "#FFFFFF",
                border: "1px solid rgba(156,79,255,0.14)",
                color: PURPLE,
                fontFamily: "Be Vietnam Pro",
                fontWeight: 700,
                px: 1,
              }}
            />
          </Stack>
        </Box>

        {loading ? (
          <Paper
            elevation={0}
            sx={{
              minHeight: 360,
              borderRadius: "28px",
              border: "1px solid rgba(15, 23, 42, 0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <CircularProgress size={32} sx={{ color: PURPLE }} />
            <Typography sx={{ fontFamily: "Be Vietnam Pro", color: "#475467" }}>
              Loading candidate profile...
            </Typography>
          </Paper>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : candidate ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.4fr) minmax(360px, 0.9fr)" },
              gap: { xs: 2.5, lg: 3 },
              alignItems: "start",
            }}
          >
            <Stack spacing={2.5}>
              <Paper
                elevation={0}
                sx={{
                  overflow: "hidden",
                  borderRadius: "28px",
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  backgroundColor: "#FFFFFF",
                }}
              >
                <Box
                  sx={{
                    p: { xs: 2.5, sm: 3.5 },
                    background:
                      "linear-gradient(135deg, rgba(52,81,244,0.08) 0%, rgba(156,79,255,0.12) 100%)",
                    borderBottom: "1px solid rgba(15,23,42,0.06)",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2.5}
                    sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
                  >
                    <Avatar
                      src={safeTrim(candidate.avatarUrl)}
                      alt={candidate.fullName}
                      sx={{
                        width: 84,
                        height: 84,
                        border: "4px solid rgba(255,255,255,0.75)",
                        boxShadow: "0 16px 30px rgba(15, 23, 42, 0.12)",
                        background: "linear-gradient(135deg, #3451F4 0%, #9C4FFF 100%)",
                        fontFamily: "Be Vietnam Pro",
                        fontSize: 30,
                        fontWeight: 800,
                      }}
                    >
                      {(candidate.fullName || "?").charAt(0).toUpperCase()}
                    </Avatar>

                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontFamily: "Be Vietnam Pro",
                          fontSize: { xs: 26, sm: 32 },
                          lineHeight: 1.08,
                          fontWeight: 900,
                          color: "#101828",
                          mb: 1,
                        }}
                      >
                        {candidate.fullName || "Shared Candidate"}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "Be Vietnam Pro",
                          fontSize: { xs: 15, sm: 18 },
                          lineHeight: 1.6,
                          color: "#344054",
                          maxWidth: 720,
                          mb: 1.5,
                        }}
                      >
                        {share?.headline || candidate.summary}
                      </Typography>

                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {[candidate.title, candidate.currentCompany, candidate.location]
                          .map((item) => safeTrim(item))
                          .filter(Boolean)
                          .map((item) => (
                            <Chip
                              key={item}
                              label={item}
                              sx={{
                                height: 32,
                                borderRadius: "999px",
                                backgroundColor: "#FFFFFF",
                                border: "1px solid rgba(15,23,42,0.08)",
                                fontFamily: "Be Vietnam Pro",
                                fontWeight: 700,
                                color: "#344054",
                              }}
                            />
                          ))}
                      </Stack>
                    </Box>
                  </Stack>
                </Box>

                <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                  <Typography
                    sx={{
                      fontFamily: "Be Vietnam Pro",
                      fontSize: 14,
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      color: BLUE,
                      mb: 1,
                    }}
                  >
                    Profile Overview
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Be Vietnam Pro",
                      fontSize: 15,
                      lineHeight: 1.8,
                      color: "#344054",
                    }}
                  >
                    {candidate.summary || "This candidate was shared for a possible introduction."}
                  </Typography>

                  {highlights.length ? (
                    <>
                      <Divider sx={{ my: 2.5 }} />
                      <Typography
                        sx={{
                          fontFamily: "Be Vietnam Pro",
                          fontSize: 14,
                          fontWeight: 800,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          color: BLUE,
                          mb: 1.25,
                        }}
                      >
                        At a Glance
                      </Typography>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {highlights.map((item) => (
                          <Chip
                            key={item}
                            label={item}
                            sx={{
                              minHeight: 34,
                              borderRadius: "999px",
                              backgroundColor: "#F9FAFB",
                              border: "1px solid rgba(15,23,42,0.08)",
                              color: "#344054",
                              fontFamily: "Be Vietnam Pro",
                              fontWeight: 600,
                            }}
                          />
                        ))}
                      </Stack>
                    </>
                  ) : null}
                </Box>
              </Paper>

              {topSkills.length ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: "24px",
                    border: "1px solid rgba(15, 23, 42, 0.08)",
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Be Vietnam Pro",
                      fontSize: 14,
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      color: BLUE,
                      mb: 1.5,
                    }}
                  >
                    Core Skills
                  </Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {topSkills.map((skill) => (
                      <Chip
                        key={skill}
                        label={skill}
                        sx={{
                          height: 34,
                          borderRadius: "999px",
                          backgroundColor: "#EEF4FF",
                          color: BLUE,
                          fontFamily: "Be Vietnam Pro",
                          fontWeight: 700,
                        }}
                      />
                    ))}
                  </Stack>
                </Paper>
              ) : null}

              {Array.isArray(candidate.experience) && candidate.experience.length ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: "24px",
                    border: "1px solid rgba(15, 23, 42, 0.08)",
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Be Vietnam Pro",
                      fontSize: 14,
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      color: BLUE,
                      mb: 1.5,
                    }}
                  >
                    Selected Experience
                  </Typography>

                  <Stack spacing={1.5}>
                    {candidate.experience.map((item, index) => (
                      <Box
                        key={`${item.company || "experience"}-${item.title || index}`}
                        sx={{
                          p: 2,
                          borderRadius: "18px",
                          backgroundColor: "#FAFAFF",
                          border: "1px solid rgba(156,79,255,0.10)",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "Be Vietnam Pro",
                            fontSize: 17,
                            fontWeight: 800,
                            color: "#101828",
                            mb: 0.35,
                          }}
                        >
                          {item.title || "Experience"}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "Be Vietnam Pro",
                            fontSize: 14,
                            fontWeight: 700,
                            color: PURPLE,
                            mb: 0.65,
                          }}
                        >
                          {[item.company, formatTimeline(item.startDate, item.endDate)]
                            .map((value) => safeTrim(value))
                            .filter(Boolean)
                            .join("  |  ")}
                        </Typography>
                        {item.location ? (
                          <Typography
                            sx={{
                              fontFamily: "Be Vietnam Pro",
                              fontSize: 13,
                              color: "#667085",
                              mb: item.summary ? 0.75 : 0,
                            }}
                          >
                            {item.location}
                          </Typography>
                        ) : null}
                        {item.summary ? (
                          <Typography
                            sx={{
                              fontFamily: "Be Vietnam Pro",
                              fontSize: 14,
                              lineHeight: 1.75,
                              color: "#344054",
                              whiteSpace: "pre-line",
                            }}
                          >
                            {item.summary}
                          </Typography>
                        ) : null}
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              ) : null}

              {Array.isArray(candidate.education) && candidate.education.length ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: "24px",
                    border: "1px solid rgba(15, 23, 42, 0.08)",
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Be Vietnam Pro",
                      fontSize: 14,
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      color: BLUE,
                      mb: 1.5,
                    }}
                  >
                    Education
                  </Typography>

                  <Stack spacing={1.25}>
                    {candidate.education.map((item, index) => (
                      <Box
                        key={`${item.school || "education"}-${item.degree || index}`}
                        sx={{
                          p: 2,
                          borderRadius: "18px",
                          border: "1px solid rgba(15,23,42,0.08)",
                          backgroundColor: "#FFFFFF",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "Be Vietnam Pro",
                            fontSize: 16,
                            fontWeight: 800,
                            color: "#101828",
                          }}
                        >
                          {item.school || "Education"}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "Be Vietnam Pro",
                            fontSize: 14,
                            color: "#475467",
                            mt: 0.45,
                          }}
                        >
                          {[item.degree, item.field, item.endDate]
                            .map((value) => safeTrim(value))
                            .filter(Boolean)
                            .join("  |  ")}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              ) : null}
            </Stack>

            <Paper
              elevation={0}
              sx={{
                position: { lg: "sticky" },
                top: { lg: 24 },
                p: { xs: 2.5, sm: 3 },
                borderRadius: "28px",
                border: "1px solid rgba(15, 23, 42, 0.08)",
                backgroundColor: "#FFFFFF",
                boxShadow: "0 20px 40px rgba(15, 23, 42, 0.06)",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Be Vietnam Pro",
                  fontSize: 13,
                  fontWeight: 800,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: PURPLE,
                  mb: 0.75,
                }}
              >
                Request an Introduction
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Be Vietnam Pro",
                  fontSize: 26,
                  lineHeight: 1.15,
                  fontWeight: 900,
                  color: "#101828",
                  mb: 1.25,
                }}
              >
                Interested in meeting this candidate?
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Be Vietnam Pro",
                  fontSize: 14,
                  lineHeight: 1.75,
                  color: "#475467",
                  mb: 2.25,
                }}
              >
                Share a few details about your team and role. Our RPO AI team will review the
                request and coordinate the introduction through the platform.
              </Typography>

              {submitError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {submitError}
                </Alert>
              ) : null}

              {submitSuccess ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {submitSuccess}
                </Alert>
              ) : null}

              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={1.5}>
                  <TextField
                    required
                    label="Your name"
                    value={form.requesterName}
                    onChange={handleChange("requesterName")}
                    sx={formFieldSx}
                  />
                  <TextField
                    required
                    label="Work email"
                    type="email"
                    value={form.requesterEmail}
                    onChange={handleChange("requesterEmail")}
                    sx={formFieldSx}
                  />
                  <TextField
                    required
                    label="Company name"
                    value={form.companyName}
                    onChange={handleChange("companyName")}
                    sx={formFieldSx}
                  />
                  <TextField
                    label="Title"
                    value={form.requesterTitle}
                    onChange={handleChange("requesterTitle")}
                    sx={formFieldSx}
                  />
                  <TextField
                    label="Phone"
                    value={form.requesterPhone}
                    onChange={handleChange("requesterPhone")}
                    sx={formFieldSx}
                  />
                  <TextField
                    label="Company website"
                    value={form.companyWebsite}
                    onChange={handleChange("companyWebsite")}
                    sx={formFieldSx}
                  />
                  <TextField
                    label="What would you like to discuss?"
                    multiline
                    minRows={5}
                    value={form.message}
                    onChange={handleChange("message")}
                    placeholder="Share the role, team context, or why you'd like to meet this candidate."
                    sx={formFieldSx}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={submitting}
                    sx={{
                      minHeight: 50,
                      mt: 0.5,
                      borderRadius: "999px",
                      background: "linear-gradient(135deg, #3451F4 0%, #9C4FFF 100%)",
                      fontFamily: "Be Vietnam Pro",
                      fontSize: 14,
                      fontWeight: 800,
                      textTransform: "none",
                      boxShadow: "0 18px 30px rgba(52, 81, 244, 0.18)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #3451F4 0%, #9C4FFF 100%)",
                      },
                    }}
                  >
                    {submitting ? "Sending Request..." : "Request Introduction"}
                  </Button>
                </Stack>
              </Box>
            </Paper>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
